import { useEffect, useMemo } from 'react'

import { useRequest } from 'ahooks'
import { isNil } from 'lodash'

import { queryTemplateApi } from '@eastcoal/api/contract/template/queryTemplate'
import { queryTemplateVariableListApi } from '@eastcoal/api/contract/templateVariable/queryTemplateVariableList'

import { useParsedSearch } from '@eastcoal/common/hooks/useParsedSearch'
import { KeySet, Noop } from '@eastcoal/common/types/utilities'

import { TemplateVariableType } from '@eastcoal/shared/enums/TemplateVariableType'

import {
  TABLE_VARIABLE_NODE_INDEX_COL_PROPERTY,
  TABLE_VARIABLE_NODE_INDEX_COL_TEXT
} from '../../../editor/nodes/TableVariableNode/constants'
import { useContractDesignerStoreApi } from '../hooks/useStore'
import {
  ContractDesignerAction,
  ContractDesignerState
} from '../store/interface'

const useStoreUpdate = <K extends KeySet<ContractDesignerState>>(
  key: K,
  value: ContractDesignerState[K],
  condition?: boolean
) => {
  const { setState } = useContractDesignerStoreApi()
  useEffect(() => {
    if (condition !== false) {
      setState({ [key]: value })
    }
  }, [key, value, condition, setState])
}

const useStoreSetter = <K extends KeySet<ContractDesignerAction>>(
  setter: K,
  value: Parameters<ContractDesignerAction[K]>[0]
) => {
  const { getState } = useContractDesignerStoreApi()
  useEffect(() => {
    const setFn = getState()[setter] as Noop
    setFn(value)
  }, [setter, value, getState])
}

export const ContractDesignerStoreUpdater = () => {
  // 获取模板详情
  const urlParams = useParsedSearch(window.location.search)
  const { id } = urlParams
  const {
    data: formData,
    loading: formLoading,
    error: formError
  } = useRequest(() => queryTemplateApi({ id }), {
    manual: false,
    ready: !!id,
    refreshDeps: [id]
  })

  useStoreSetter('setInitialFormData', urlParams)
  useStoreUpdate('formData', formData as any, formData !== undefined)
  useStoreUpdate('formLoading', formLoading)
  useStoreUpdate('formError', formError)

  const bizType = formData?.bizType ?? urlParams.bizType

  const {
    data: templateVariableData,
    loading: templateVariableLoading,
    error: templateVariableError,
    refreshAsync: refreshTemplateVariableData
  } = useRequest(() => queryTemplateVariableListApi({ bizType }), {
    manual: false,
    ready: !isNil(bizType),
    // bizType 有可能是数字跟字符串，会导致重复请求
    refreshDeps: [bizType?.toString()]
  })

  const customizeTemplateVariableData = useMemo(() => {
    return (
      templateVariableData?.map(item => {
        if (item.type === TemplateVariableType.TABLE) {
          item.children = [
            {
              id: TABLE_VARIABLE_NODE_INDEX_COL_PROPERTY,
              property: TABLE_VARIABLE_NODE_INDEX_COL_PROPERTY,
              name: TABLE_VARIABLE_NODE_INDEX_COL_TEXT,
              description: '自动递增的序号'
            },
            ...(item.children || [])
          ]
        }
        return item
      }) ?? []
    )
  }, [templateVariableData])
  useStoreUpdate('templateVariableData', customizeTemplateVariableData)
  useStoreUpdate('templateVariableLoading', templateVariableLoading)
  useStoreUpdate('templateVariableError', templateVariableError)
  useStoreUpdate('refreshTemplateVariableData', refreshTemplateVariableData)

  return null
}
